import * as fmt from "../ui/fmt.ts";
import type { WeaponRef } from "./common.ts";
import type { Git } from "./Git.ts";
import {
  Ruleset,
  type VehicleWeaponRef,
  type WeaponProfile,
} from "./Ruleset.ts";
import type { Vehicle } from "./Vehicle.ts";

type WeaponOwner = Git | Vehicle | Ruleset;

export class Weapon<T extends WeaponOwner = WeaponOwner> {
  owner: T;
  data: WeaponProfile;

  constructor(owner: T, data: WeaponProfile) {
    this.owner = owner;
    this.data = data;
  }

  get id() {
    return this.data.id;
  }

  get name() {
    return this.data.name;
  }

  get ruleset(): Ruleset {
    return this.owner instanceof Ruleset ? this.owner : this.owner.ruleset;
  }

  get cost() {
    return this.data.cost;
  }

  get isRanged() {
    return this.data.range > 0;
  }

  get range() {
    return !this.isRanged ? "—" : `${this.data.range}″`;
  }

  get dice() {
    return fmt.diceRolls(this.data.dice);
  }

  get damage() {
    return fmt.damage(this.data.damage);
  }

  get hands() {
    return this.data.hands;
  }

  get stats() {
    const base = `Dice: ${this.dice} · Damage: ${this.damage}`;

    if (this.isRanged) {
      return `Range: ${this.range} · ${base}`;
    }

    return base;
  }

  get rules() {
    return this.data.rules.map((ref) => {
      return this.ruleset.weaponSpecialRules.get(ref.id);
    });
  }

  get sortValue() {
    // Sorting based on how weapons are defined in the ruleset
    const sortTable = Object.fromEntries(
      this.ruleset.weapons.toArray().map((w, index) => {
        return [w.id, index];
      }),
    );

    return sortTable[this.id] ?? 0;
  }

  compare(that: Weapon) {
    return this.sortValue - that.sortValue;
  }
}

export class GitWeapon extends Weapon<Git> {
  ref: WeaponRef;
  index: number;

  constructor(git: Git, index: number, ref: WeaponRef, data: WeaponProfile) {
    super(git, data);
    this.index = index;
    this.ref = ref;
  }

  get git() {
    return this.owner;
  }

  get count() {
    return this.ref.count;
  }

  get nameWithCount() {
    return this.count > 1 ? `${this.name} x${this.count}` : this.name;
  }

  static fromHeadbutt(git: Git) {
    const ref = { id: "headbutt", count: 1 };
    return new GitWeapon(git, 0, ref, {
      id: "headbutt",
      name: "Headbutt",
      cost: 0,
      damage: git.ruleset.diceTypeToDamage[git.headbutt],
      dice: { type: git.headbutt, count: 1 },
      hands: 0,
      range: 0,
      rules: [],
      selectOnGit: [],
      selectOnVehicle: [],
    });
  }
}

export class VehicleWeapon extends Weapon<Vehicle> {
  ref: VehicleWeaponRef;
  index: number;

  constructor(
    git: Vehicle,
    index: number,
    ref: VehicleWeaponRef,
    data: WeaponProfile,
  ) {
    super(git, data);
    this.index = index;
    this.ref = ref;
  }

  get vehicle() {
    return this.owner;
  }

  get count() {
    return this.ref.count;
  }

  get nameWithCount() {
    return this.count > 1 ? `${this.name} x${this.count}` : this.name;
  }

  get placement() {
    return fmt.placement(this.ref);
  }

  get placementSortValue() {
    return this.ref.placement === "FIXED" ? 1 : 2;
  }

  /**
   * Like Weapon#compare, but places fixed weapons above all others.
   *
   * This is useful when listing all vehicle weapons next to each other. In
   * cases where you want to compare a heterogenous list of weapons, use
   * {@link compare} instead.
   */
  vehicleWeaponCompare(that: VehicleWeapon) {
    if (this.placementSortValue !== that.placementSortValue) {
      return this.placementSortValue - that.placementSortValue;
    }

    return super.compare(that);
  }
}
